export interface FieldEditorProps
{
    /** The type of the field editor. */
    type: FieldEditorType;
    /** The name of the field. */
    field: string;
    /** The label for the editor. */
    label?: string;
    /** The position of the label. */
    labelPosition?: FieldEditorLabelPosition;
    /** Is this field required? */
    isRequired?: boolean;
    /** Is this field visible? */
    isVisible?: boolean;
    /** Is the editing enabled? */
    isEnabled?: boolean;
    /** The placeholder for the field value. */
    placeholder?: string;
    /** Insert an element before the input element. */
    beforeInput?: React.ReactNode
    /** Insert an element after the input element. */
    afterInput?: React.ReactNode
    /** Show a search box for the list editor. */
    showSearchBoxForList?: boolean;
    /** Show the selected item badges for the list editor. */
    showSelectedItemsForList?: boolean;
    /** CSS class for the control. */
    className?: string;
    /** The datasource for Select, List or listlike editors. */
    dataSource?: { [index: string]: any }[];
    /** The datasource object's property that is human readable. */
    dataSourceDisplayMember?: string;
    /** The datasource object's property that is used as the real value for the field. */
    dataSourceValueMember?: string;
    /** Hide the the empty option in the select control. */
    hideSelectEmpty?: boolean;
    /** The maximum length of the input text. */
    maxLength?: number;
    /** The minimum value of a number, range, date, datetime-local, month, time and week field. */
    min?: string | number;
    /** The maximum value of a number, range, date, datetime-local, month, time and week field. */
    max?: string | number;
    /** Optional mask format for the phone input type. Defaults to '(000) 000-0000'. */
    phoneMask?: string;
    /** The number of rows in a text area. */
    textareaRows?: number;
    /** The invalid feedback message. */
    invalidFeedbackMessage?: string;
    /** Allow multiple file upload for an File editor control (needs a FileList object). */
    allowMultipleFileUpload?: boolean;
    /** The reference to the file input element. Use this with useRef() to clear the file input value. (fileInputRef.current!.value = '') */
    fileInputRef?: any;
    /** The value of the field. */
    value?: any;
    /** Fired when the field value changes in the editor. In case of Select/List based on a generated value/display objects and you don't need the underlying generated object just use the value field: value?.value */
    onValueChanged?: (field: string, value: any) => void;
    /** Fired when a key is pressed down */
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    /** Async function to load options for AutoComplete based on input. 
     * The options need to have the fields for dataSourceValueMember and dataSourceDisplayMember. 
     * No need to set the dataSource property if this is provided. Actually, it will be ignored.
     */
    loadAutocompleteOptions?: (inputValue: string) => Promise<any[] | undefined>;
}

export enum FieldEditorType
{
    Text = 'text',
    Email = 'email',
    Phone = 'phone',
    /** Numeric input with visible increment/decrement buttons for integers. */
    NumericStepper = 'numeric-stepper',
    Password = 'password',
    Number = 'number',
    /** Accepts a list of objects. Single selection. */
    Select = 'select',
    /** Accepts a list of objects. Multiple selection. */
    List = 'list',
    /** Autocomplete input with MUI Autocomplete. */
    AutoComplete = 'autocomplete',
    /** Multiple selectable values from dropdown. Selected values are displayed as badges. */
    MultiSelect = 'multi-select',
    Date = 'date',
    Time = 'time',
    DateTimeLocal = 'datetime-local',
    Textarea = 'textarea',
    RichTextEditor = 'rich-text-editor',
    Checkbox = 'checkbox',
    TripleStateCheckbox = 'triple-state-checkbox',
    File = 'file',
    /** Color picker with hex input support. */
    ColorPicker = 'color-picker',
}

export enum FieldEditorLabelPosition
{
    Vertical,
    Horizontal,
    Floating,
    Hidden
}

/**
 * The data for a field editor. These are set via the data attributes of the field editor component.
 */
export type FieldEditorData = {
    type?: FieldEditorType;
    field?: string;
    label?: string;
    labelPosition?: FieldEditorLabelPosition;
    placeholder?: string;
    isRequired?: boolean;
    isVisible?: boolean;
    isEnabled?: boolean;
}

export default FieldEditorProps;